import { v3 } from "../short3";
import * as Doms from "oc-ui/Doms";
import * as Scenes from "oc-threejs/Scenes";

import * as _ from "lodash";
import {
  Color,
  Group,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  SRGBColorSpace,
  Vector3,
} from "three";

declare const APP: any;

/**
 * zeigt ein Kugel-Raster an, das verschiedene Materialparameter zeigt, um Lichtsetup zu eichen
 */
export class OcMaterialHelper extends Group {
  constructor(size: number = 1) {
    super();
    this.name = "OcMaterialHelper";
    const count = 8;

    this.addLuminescenceRoughnessRaster(count, size * 0.4, 0, v3(0, 0, 0));
    // this.addLuminescenceRoughnessRaster(count, size*0.4, 1, v3(size*0.5,0,0));
    this.addSpecialMaterials(count, size * 0.4, v3(size * 0.5, 0, 0));
    this.addHueLuminescenceRaster(count, size * 0.4, 0.5, v3(0, size * 0.5, 0));
    this.addHueLuminescenceRaster(count, size * 0.4, 1, v3(size * 0.5, size * 0.5, 0));
    this.showDomHelper();
  }

  showDomHelper(): void {
    const e = Doms.createElementFromHTML(
      `<div style="position:absolute;left:0px;top:50px;width:50px;height:50px;z-index:10000;border:1px solid red;pointer-events: none;">`,
    );
    e.addEventListener("click", (ev: MouseEvent) => e.remove());
    document.body.appendChild(e);
    this.children.forEach((c: any) => {
      const x = 100 * c.position.x;
      const y = 100 * (1 - c.position.y);
      const w = 100 / 8;
      const colorCss = c.material.userData._colorString ?? "white"; //c.material.color.getStyle(THREE.SRGBColorSpace);
      e.appendChild(
        Doms.createElementFromHTML(
          `<div style="position:absolute;left:${x}%;top:${y}%;width:${w}%;height:${w}%;background-color:${colorCss}">`,
        ),
      );
    });
  }

  addHueLuminescenceRaster(
    count: number,
    size: number,
    saturation: number,
    positionOffset: Vector3,
  ): void {
    const countY = count - 1;
    const countX = count;
    const sphereSize = size / countY;
    const roughness = 0.7;
    this.range(0, 1, countX, (from, to, count, luminescence) => {
      this.range(0, 1, countY, (from, to, count, hue) => {
        const color = this.color(hue, saturation, luminescence);
        this.setPosition(
          this.addSphere(sphereSize * 0.5, this.standardMaterial(color, 0, roughness)),
          v3(luminescence * size, hue * (size - sphereSize) + sphereSize, 0).add(positionOffset),
        );
      });
      const color = this.color(0, saturation, luminescence);
      this.setPosition(
        this.addSphere(sphereSize * 0.5, this.basicMaterial(color)),
        v3(luminescence * size, sphereSize * count, 0)
          .add(positionOffset)
          .add(v3(0, sphereSize * 0.1, 0)),
      );
    });
  }

  addLuminescenceRoughnessRaster(
    count: number,
    size: number,
    metalness: number,
    positionOffset: Vector3,
  ): void {
    const countY = count - 1;
    const countX = count;
    const sphereSize = size / countY;
    this.range(0, 1, countX, (from, to, count, luminescence) => {
      const color = this.color(0, 0, luminescence);
      this.range(1, 0, countY, (from, to, count, roughness) => {
        this.setPosition(
          this.addSphere(sphereSize * 0.5, this.standardMaterial(color, metalness, roughness)),
          v3(luminescence * size, roughness * (size - sphereSize) + sphereSize, 0).add(
            positionOffset,
          ),
        );
      });
      this.setPosition(
        this.addSphere(sphereSize * 0.5, this.basicMaterial(color)),
        v3(luminescence * size, sphereSize * count, 0)
          .add(positionOffset)
          .add(v3(0, sphereSize * 0.1, 0)),
      );
    });
  }

  addSpecialMaterials(count: number, size: number, positionOffset: Vector3): void {
    const materialRanges = [
      {
        prop: "roughness",
        min: 0,
        max: 1,
        values: {
          color: 0xffffff,
          name: "Metal",
          metalness: 1,
        },
      },
      {
        prop: "roughness",
        min: 0,
        max: 1,
        values: {
          color: 0xffd700,
          name: "Metal",
          metalness: 1,
        },
      },
      {
        prop: "transmission",
        min: 0,
        max: 1,
        values: {
          color: 0xf0f0f0,
          name: "Glas",
          reflectivity: 0.892903059912574,
          roughness: 0.0993,
          thickness: 0.3439,
          transmission: 0.9574,
        },
      },
      {
        prop: "roughness",
        min: 0,
        max: 1,
        values: {
          color: 0xf0f0f0,
          name: "Glas",
          reflectivity: 0.892903059912574,
          roughness: 0.0993,
          thickness: 0.3439,
          transmission: 0.9574,
        },
      },
      {
        prop: "reflectivity",
        min: 0,
        max: 1,
        values: {
          color: 0xf0f0f0,
          name: "Glas",
          reflectivity: 0.892903059912574,
          roughness: 0.0993,
          thickness: 0.3439,
          transmission: 0.9574,
        },
      },
      {
        prop: "thickness",
        min: 0,
        max: 1,
        values: {
          color: 0xf0f0f0,
          name: "Glas",
          reflectivity: 0.892903059912574,
          roughness: 0.0993,
          thickness: 0.3439,
          transmission: 0.9574,
        },
      },
      {
        prop: "clearcoatRoughness",
        min: 0,
        max: 1,
        values: {
          color: 0xff0000,
          name: "Lack",
          clearcoat: 0.8,
          roughness: 0.5,
          metalness: 1,
        },
      },
      {
        prop: "clearcoat",
        min: 0,
        max: 1,
        values: {
          color: 0x0000ff,
          name: "Lack",
          clearcoatRoughness: 0.05,
          roughness: 0.5,
          metalness: 1,
        },
      },
    ];
    const countY = count;
    const countX = count;
    const sphereSize = size / (countY - 1);
    this.range(1, 0, countX, (from, to, count, fx, x) => {
      this.range(count / (count - 1), 0, countY, (from, to, count, fy, y) => {
        const r = materialRanges[y % materialRanges.length];
        const values: any = _.cloneDeep(r.values);
        values[r.prop] = r.min * (1 - fx) + r.max * fx;
        values.name = values.name + "_" + x + "_" + y;
        this.setPosition(
          this.addSphere(sphereSize * 0.5, this.physicalMaterial(values)),
          v3(fx * size, fy * (size - sphereSize) + sphereSize, 0).add(positionOffset),
        );
      });
    });
  }

  setPosition(object: Object3D, position: Vector3): Object3D {
    object.position.copy(position);
    return object;
  }

  color(h: number, s: number, l: number): Color {
    const color: any = new Color().setHSL(h, s, l, SRGBColorSpace);
    color._colorString = `hsl(${h.toFixed(0)},${(s * 100).toFixed(0)}%,${(l * 100).toFixed(0)}%)`;
    return color;
  }

  basicMaterial(color: any): MeshBasicMaterial {
    return this.copyColor(new MeshBasicMaterial({ color }), color);
  }

  standardMaterial(color: any, metalness: number, roughness: number): MeshStandardMaterial {
    return this.copyColor(new MeshStandardMaterial({ color, metalness, roughness }), color);
  }

  physicalMaterial(props: any): MeshPhysicalMaterial {
    return this.copyColor(new MeshPhysicalMaterial({ ...props }), props.color);
  }

  copyColor(material: any, color: any): any {
    material.userData._colorString = color._colorString;
    return material;
  }

  addSphere(size: number, material: Material): Mesh {
    const sphere = Scenes.sphere(size, material);
    this.add(sphere);
    return sphere;
  }

  range(
    from: number,
    to: number,
    count: number,
    iterator: (from: number, to: number, count: number, value: number, index: number) => void,
  ): void {
    for (let i = 0; i < count; i++) {
      const fraction = i / (count - 1);
      const lerp = (to - from) * fraction + from;
      iterator(from, to, count, lerp, i);
    }
  }
}
